<template>
	<div class="mob-stock-table-container">
		<TableTab :tabList="otherDataTabList" :activeValue="activeTab" @change="changeTableTab" />

		<div v-if="activeTab" v-loading="loading">
			<MiniTable :data="pickCurrTableData" :key="activeTab">
				<!-- 分红送配 -->
				<template v-if="activeTab === 'dividendPlan'">
					<MiniTableColumn prop="publishDate" :label="$t('enterprise.announced')" width="100" />
					<MiniTableColumn prop="recordDate" :label="$t('enterprise.record-date')" width="120" />
					<MiniTableColumn prop="dividendDate" :label="$t('enterprise.payment-date')" width="120" />
					<MiniTableColumn prop="dividendCash" :label="$t('enterprise.dividend-scheme')" width="150" />
					<MiniTableColumn
						prop="perShareDivRatio"
						:label="$t('enterprise.bonus-issue/by-reserve')"
						width="150"
					>
						<template #default="{ row }">
							<span
								>{{ formatDefault(row.perShareDivRatio) }}/{{
									formatDefault(row.perShareTransRatio)
								}}</span
							>
						</template>
					</MiniTableColumn>
					<MiniTableColumn prop="exDivDate" :label="$t('enterprise.ex-date')" width="120" />
				</template>
				<!-- 北上/南下资金 -->
				<template v-if="activeTab === 'northSouthFunds'">
					<MiniTableColumn prop="tradingDay" :label="$t('enterprise.holding-date')" width="100" />
					<MiniTableColumn prop="closePrice" :label="$t('enterprise.closing-price')" width="180">
						<template #default="{ row }">
							<span>{{ formatterColTransferThousand(row, { property: 'closePrice' }) }}</span>
						</template>
					</MiniTableColumn>
					<MiniTableColumn prop="changePct" :label="$t('enterprise.daily-change')" width="180" />
					<MiniTableColumn
						prop="holdVol"
						:label="$t('enterprise.number-of-shares-held-(shares)')"
						width="180"
					>
						<template #default="{ row }">
							<span>{{ formatterColTransferUnit(row, { property: 'holdVol' }) }}</span>
						</template>
					</MiniTableColumn>
					<MiniTableColumn prop="holdPct" :label="$t('enterprise.holding-percentage')" width="180" />
				</template>
			</MiniTable>
			<FooterLoadPlaceholder :more="hasMoreData" @loadMore="onPullUp" />
		</div>
	</div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, watch, onMounted } from 'vue';
import { usePaginationMobile } from '@rebuild/hooks/load-more/usePaginationMobile';
import { formatDefault, isNonValue, convertNumberToCNUnit } from '@/utils/api';
import i18n, { EN } from '@/locales';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { MiniTable, MiniTableColumn } from '@/rebuild/components/table';
import FooterLoadPlaceholder from '@/rebuild/components/FooterLoadPlaceholder.vue';
import TableTab from '@rebuild/materials/shared/quote/mobile/TableTab.vue';

const props = defineProps({
	fullCode: {
		type: String,
		default: ''
	}
});

const { proxy } = getCurrentInstance();

// 判断是否为港股
const isHKStock = computed(() => {
	return props.fullCode?.toUpperCase().includes('HK');
});

const activeTab = ref('');
const otherDataTabList = computed(() => {
	return [
		{
			label: proxy.$t('enterprise.dividends'),
			value: 'dividendPlan'
		},
		{
			label: isHKStock.value
				? proxy.$t('enterprise.southbound-capital')
				: proxy.$t('enterprise.northbound-funds'),
			value: 'northSouthFunds'
		}
	];
});

const changeTableTab = result => {
	activeTab.value = result.value;
};

const handlePaginationChange = () => {
	updateDataTab({ value: activeTab.value });
};
const {
	currentPage,
	pageSize,

	finalData,
	setData,
	hasMoreData,
	onPullUp,
	refreshPagination
} = usePaginationMobile({
	bindChange: handlePaginationChange
});

const { quote_stockDividendPlan, quote_stockNorthSouthFunds } = useModuleData();

const queryDividendPlan = async () => {
	await quote_stockDividendPlan.refresh({
		fullCode: props.fullCode
	});
	return {
		prop: 'dividendPlan',
		data: quote_stockDividendPlan.data.data || []
	};
};

const queryMorthSouthFunds = async () => {
	await quote_stockNorthSouthFunds.refresh({
		fullCode: props.fullCode,
		pageStart: currentPage.value - 1,
		pageNum: pageSize.value
	});
	return {
		prop: 'northSouthFunds',
		data: quote_stockNorthSouthFunds.data.data || [],
		total: quote_stockNorthSouthFunds.data.total || 0
	};
};

const pickCurrTableData = computed(() => {
	return finalData.value || [];
});

function recognizeUnit(numStr) {
	var num = parseFloat(numStr);
	if (i18n.locale === EN) {
		const { value, unit } = convertNumberToCNUnit(num);
		return `${value}${unit}`;
	}

	var unit = proxy.$t('enterprise.ten-thousand');
	var value = num / 10000;
	if (num >= 100000000) {
		unit = proxy.$t('enterprise.one-hundred-million');
		value = num / 100000000;
	}
	return `${value.toFixed(2)}${unit}`;
}
const formatterColTransferUnit = (row, column) => {
	const nonValue = isNonValue(row[column.property]);
	if (nonValue) return formatDefault(row[column.property]);
	return recognizeUnit(row[column.property]);
};
function formatDecimal(inputString) {
	// 判断字符串中是否包含小数点
	if (inputString.includes('.')) {
		// 分割小数点前后的部分
		const [integerPart, decimalPart] = inputString.split('.');

		// 补零，确保小数点后至少有两位数
		let formattedDecimal = decimalPart;
		if (formattedDecimal.length === 1) {
			formattedDecimal += '0';
		}

		// 拼接整数部分和格式化后的小数部分
		return `${integerPart}.${formattedDecimal}`;
	} else {
		// 不包含小数点，直接返回原字符串
		return inputString;
	}
}
function toThousandSeparator(num) {
	const value = Number(num).toLocaleString();
	return formatDecimal(value);
}
const formatterColTransferThousand = (row, column) => {
	const nonValue = isNonValue(row[column.property]);
	if (nonValue) return formatDefault(row[column.property]);
	return toThousandSeparator(row[column.property]);
};

const loading = ref(false);

const updateDataTab = async ({ value }) => {
	loading.value = true;
	try {
		if (value == 'dividendPlan') {
			const res = await queryDividendPlan();
			setData(res.data, 0);
		} else if (value == 'northSouthFunds') {
			const res = await queryMorthSouthFunds();
			setData(res.data, res.total);
			return res.total;
		}
	} finally {
		loading.value = false;
	}
};

onMounted(() => {
	activeTab.value = otherDataTabList.value[0].value;
});

// 监听 activeTab 变化，更新数据
watch(activeTab, newVal => {
	if (newVal) {
		refreshPagination();
	}
});
</script>

<style lang="less" scoped>
.c-stock-table {
	position: relative;
}
.unit-tips {
	font-size: 14px;
	font-weight: 400;
	color: #86909c;
	height: 55px;
	line-height: 55px;

	position: absolute;
	top: 0;
	right: 0;
}
.el-table {
	/deep/th.el-table__cell {
		.cell {
			color: #fff !important;
		}
	}
}
</style>
